'use strict';
import angular from 'angular';
import $ from 'jquery';
import moment from 'moment';
import _ from 'lodash';

import {$DateRangeEvents} from 'coreModules/daterange/base/daterange.constants';
import {WeeklyStartDay} from 'coreModules/design/widget/design.widget.constants';

angular.module('core.app.services', [])
    .service('AppFactory', AppFactory)
    .service('AppService', AppService);

/**
 * App factory (exposes app wide variables externally)
 * @ngInject
 */
function AppFactory(
    PubSub,
    UserType,
    UserThemes,
    MomentDateFormat,
    RelativeDateRange,
    ColumnFormat,
    DataSourceType,
    ServiceResource,
    CategoryFactory,
    LanguageService,
    AppModule
) {
    //
    // ANGULAR GENERAL
    //

    /**
     * Function to make sure the change in watch is valid by trying to make it fail (i.e. invalidate it)
     * @param nV
     * @param oV
     */
    function invalidateWatch(nV, oV) {
        return _.isUndefined(nV) || _.isUndefined(oV) || nV == oV;
    }

    /**
     * Function to map an array into a memoized object
     * Useful for avoiding _.find() in a large object array
     * @param arr
     * @param key
     * @returns {Function}
     */
    function arrayToMemoizedObj(arr, key) {
        return (_.memoize(function() {
            return _.reduce(arr, function (accumulatedKeys, result, idx) {
                result.index = idx;
                accumulatedKeys[result[key]] = result;
                return accumulatedKeys;
            }, {});
        }))();
    }

    //
    // UTILITY FUNCTIONS /////////////////////////////////////
    //
    var util = {};

    /**
     * Function to check if a value is truly empty
     * @param value
     */
    util.isAbsolutelyEmpty = function(value) {
        return _.isUndefined(value)
            || _.isNull(value)
            || value === 'null'
            || (!_.isNumber(value) && _.isEmpty(value));
    }

    //
    // DATE
    //
    util.date = {};

    /**
     * Returns a unix timestamp from a formatted date string
     * @param date
     * @returns {number}
     */
    util.date.getUnixTimestamp = function(date) {
        return moment(date).unix();
    };

    /**
     * Returns formatted ISO date string from a unix timestamp
     * @param number
     * @returns {date}
     */
    util.date.getISOFormattedDate = function(timestamp) {
        // Ignore moment conversion if already is a moment object
        var date = _.isObject(timestamp) ? timestamp : moment.unix(parseInt(timestamp));
        return date.format('YYYY-MM-DD');
    };

    //
    // COLUMN
    //
    util.column = {};

    /**
     * Returns if a column format is of type numeric
     * @param format
     * @returns {boolean}
     */
    util.column.isNumeric = function(format) {
        return format === ColumnFormat.FORMAT_CURRENCY
            || format === ColumnFormat.FORMAT_DECIMAL
            || format === ColumnFormat.FORMAT_INTEGER
            || format === ColumnFormat.FORMAT_TIME
            || format === ColumnFormat.FORMAT_PERCENT;
    };

    /**
     * Returns if a column format is of type date
     * @param format
     * @returns {boolean}
     */
    util.column.isDate = function(format) {
        return format === ColumnFormat.FORMAT_DATE
            || format === ColumnFormat.FORMAT_DATETIME;
    };

    /**
     * Returns if a column format is of type time
     * @param format
     * @returns {boolean}
     */
    util.column.isTime = function(format) {
        return format === ColumnFormat.FORMAT_TIME;
    };

    /**
     * Returns if a column format is of type string
     * @param format
     * @returns {boolean}
     */
    util.column.isString = function(format) {
        return format === ColumnFormat.FORMAT_STRING;
    };

    /**
     * Returns if a column format is of type link
     * @param format
     * @returns {boolean}
     */
    util.column.isLink = function(format) {
        return format === ColumnFormat.FORMAT_LINK;
    };

    /**
     * Returns if a column format is of type URL
     * @param format
     * @returns {boolean}
     */
    util.column.isUrl = function(format) {
        return format === ColumnFormat.FORMAT_URL;
    };

    /**
     * Returns if a column format is of type audio
     * @param format
     * @returns {boolean}
     */
    util.column.isAudio = function(format) {
        return format === ColumnFormat.FORMAT_AUDIO;
    };

    /**
     * Returns if a column format is of type click to view link
     * @param format
     * @returns {boolean}
     */
    util.column.isClickToViewLink = function(format) {
        return format === ColumnFormat.FORMAT_CLICK_TO_VIEW_LINK;
    };

    /**
     * Returns if a column format is of type thumbnail
     * @param format
     * @returns {boolean}
     */
    util.column.isThumbnail = function(format) {
        return format === ColumnFormat.FORMAT_THUMBNAIL;
    };

    /**
     * Returns if a column format is of type preview
     * @param format
     * @returns {boolean}
     */
    util.column.isPreview = function(format) {
        return format === ColumnFormat.FORMAT_PREVIEW;

    };

    /**
     * Returns if a column format is of type ID
     * @param format
     * @returns {boolean}
     */
    util.column.isID = function(format) {
        return format === ColumnFormat.FORMAT_ID;
    };

    /**
     * Returns if a column format is of type ranking
     * @param format
     * @returns {boolean}
     */
    util.column.isRanking = function(format) {
        return format === ColumnFormat.FORMAT_RANKING;
    };

    /**
     * Returns if a column format is of type callback
     * @param format
     * @returns {boolean}
     */
    util.column.isCallback = function(format) {
        return format === ColumnFormat.FORMAT_CALLBACK;
    };

    /**
     * Returns if a column format is of type phone number
     * @param format
     * @returns {boolean}
     */
    util.column.isPhoneNumber = function(format) {
        return format === ColumnFormat.FORMAT_PHONE_NUMBER;
    };

    /**
     * Returns if a column format is of type boolean
     * @param format
     * @returns {boolean}
     */
    util.column.isBoolean = function(format) {
        return format === ColumnFormat.FORMAT_BOOLEAN;
    };

    /**
     * Returns if a column format is of type enum
     * @param format
     * @returns {boolean}
     */
    util.column.isEnum = function(format) {
        return format === ColumnFormat.FORMAT_ENUM;
    };

    /**
     * Returns if a column format is of general type text
     * @param format
     * @returns {boolean}
     */
    util.column.isText = function(format) {
        return format === ColumnFormat.FORMAT_STRING
            || format === ColumnFormat.FORMAT_ID
            || format === ColumnFormat.FORMAT_LINK
            || format === ColumnFormat.FORMAT_CLICK_TO_VIEW_LINK
            || format === ColumnFormat.FORMAT_ENUM
            || format === ColumnFormat.FORMAT_AUDIO
            || format === ColumnFormat.FORMAT_THUMBNAIL
            || format === ColumnFormat.FORMAT_PREVIEW
            || format === ColumnFormat.FORMAT_URL;
    };

    /**
     * Returns true if the column is a primary key column and have no associated name column.
     * Allow to exclude this column from the group by candidate when creating a widget.
     * @param column
     * @returns {boolean}
     */
    util.column.isOrphanPrimaryKeyColumn = function(column) {
        return column.is_primary_key && column.groupby_id_field === column.groupby_name_field
    };

    //
    // USER ////////////////////////////////////////
    //
    var user = {};

    /**
     * Sets the user var to be exposed externally
     * @param currentUser
     * @returns {*}
     */
    function setUser(currentUser) {
        user = currentUser;

        user.isSuperAdmin = function() {
            return user.type === UserType.SUPERADMIN;
        };

        user.isPredefinedSuperAdmin = function() {
            return (user.type === UserType.SUPERADMIN) && user.role?.predefined_role_key;
        };

        user.isClusterAdmin = function() {
            return user.type === UserType.CLUSTERADMIN;
        };

        user.isAdmin = function() {
            return user.type === UserType.SUPERADMIN || user.type === UserType.CLUSTERADMIN;
        };

        user.isAgent = function() {
            return user.type === UserType.AGENT;
        };

        user.isClient = function() {
            return user.type === UserType.CLIENT;
        };

        user.isClientGroup = function() {
            return user.type === UserType.CLIENT_GROUP;
        };

        // check if a feature is available for the current user
        user.isFeatureAvailable = function(featureName) {
            return _.includes(user.availableFeatures, featureName);
        };

        // check if a module is available for the current user
        user.isModuleAvailable = function(moduleName) {
            return _.includes(user.availableModules, moduleName);
        };

        user.getDefaultWeeklyStartDay = function() {
            return user.weekStartsOnMonday ? WeeklyStartDay.MONDAY : WeeklyStartDay.SUNDAY;
        };

        /**
         * Returns whether current theme is a dark or light theme
         * @returns {*}
         */
        user.getThemeType = function() {
            switch (user.themeType) {
                case UserThemes.TYPE_LIGHT:
                case UserThemes.TYPE_HYBRID: // In hybrid mode, we still use the light theme for dashboard
                    return UserThemes.TYPE_LIGHT;
                case UserThemes.TYPE_DARK:
                    return UserThemes.TYPE_DARK;
            }
        };

        user.canAccessNUIFromDropDown = () => (
            user.user.can_access_nui
          && user.isModuleAvailable(AppModule.NUI)
        );

        user.shouldUseNui = () => (
            (user.user.use_nui && user.user.can_access_nui && user.isModuleAvailable(AppModule.NUI))
            || (user.isModuleAvailable(AppModule.DISABLE_OLD_UI) && !user.isModuleAvailable(AppModule.IO_TOOL))
        );

        if (user.role) {
            Permission.setUserRole(user.role);
        }

        user.canAccessAuditLogs = () => (
            user.isPredefinedSuperAdmin()
            && user.isModuleAvailable(AppModule.DISABLE_OLD_UI)
            && ((!user.hasTapReportsFreemiumPlatformSourceLimitation
                && Permission.hasPermissionToRead(Permission.moduleName.AUDITLOG))
            || user.isRemoteLogin)
        );
    }

    function mustGoToNewApp(settings) {
        return (settings.user
          && (settings.user.use_nui && settings.user.can_access_nui && _.includes(settings.availableModules, AppModule.NUI))
          || (_.includes(settings.availableModules, AppModule.DISABLE_OLD_UI) && !user.isModuleAvailable(AppModule.IO_TOOL))
        );
    }

    /**
     * Allows read only access to the user var
     * @returns {{}}
     */
    function getUser() {
        return _.assign({}, user);
    }

    function isUserModuleAvailable (module) {
        return _.indexOf(user.availableModules, module) >= 0;
    }

    function isUserFeatureAvailable (feature) {
        return _.indexOf(user.availableFeatures, feature) >= 0;
    }

    function getInstanceTimezone () {
        return user.instanceTimezone;
    }

    /**
     * Returns the route to MDS
     * @returns {string}
     */
    function getMdsRoute() {
      return '#/service/manage';
    }

    //
    // DATA TYPES /////////////////////////////////////
    //
    var dataTypes = {}; // Refers to all possible data types built from DataType.php and Column.php

    /**
     * Sets the dataTypes var to be exposed externally
     * @param values
     * @returns {*}
     */
    function setDataTypes(values) {
        dataTypes = _.map(values, function (dataType) {
            return {
                key: dataType.key,
                value: dataType.value,
                icon: dataType.icon,
                is_selectable: dataType.is_selectable,
                is_numeric: dataType.is_numeric,
            };
        });
    }

    /**
     * Allows read only access to the dataTypes
     * @returns {{}}
     */
    function getDataTypes() {
        return _.assign({}, dataTypes);
    }

    /**
     * Returns only selectable data types i.e. display format
     * @returns {Array}
     */
    function getSelectableDataTypes() {
        return _.filter(dataTypes, {is_selectable: true});
    }

    /**
     * Returns only numeric data types
     * @returns {Array}
     */
    function getNumericDataTypes() {
        return _.filter(dataTypes, {is_numeric: true});
    }

    /**
     * Get a specific dataType by key
     * @param key
     * @returns {T|*}
     */
    function getDataType(key) {
        return _.find(dataTypes, {key: key});
    }

    /**
     * @param dataURI
     * @returns {*}
     */
    function dataURItoBlob(dataURI) {
        // convert base64/URLEncoded data component to raw binary data held in a string
        var byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0)
            byteString = atob(dataURI.split(',')[1]);
        else
            byteString = unescape(dataURI.split(',')[1]);

        // separate out the mime component
        var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to a typed array
        var ia = new Uint8Array(byteString.length);
        for (var i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ia], {type:mimeString});
    }

    //
    // BRAND MAPPINGS /////////////////////////////////////
    //
    var brandMappings = {};

    /**
     * @param mappings
     */
    function setBrandMappings(mappings) {
        brandMappings = mappings;
    }

    /**
     * @returns {{}}
     */
    function getBrandMappings() {
        return brandMappings;
    }

    //
    // DATA SOURCE TYPES /////////////////////////////////////
    //
    var dataSourceTypes = {};

    /**
     * Return dataSourceTypes
     * @returns {void|*}
     */
    var getDataSourceTypes = function() {
        return _.assign({}, dataSourceTypes);
    };


    /**
     * Set dataSourceTypes object
     * @param types
     */
    var setDataSourceTypes = function(types) {
        dataSourceTypes = types;
    };

    //
    // SERVICES /////////////////////////////////////
    //
    var services = {};

    /**
     * Sets the services var to be exposed externally
     * @param values
     * @returns {*}
     */
    function setServices(values) {
        services = _.map(values, mapService);
    }

    function mapService(service) {
        return {
            type: DataSourceType.SERVICE_DATA,
            id: service.service_id,
            name: service.name,
            text: service.name,
            color: _.isNull(service.custom_color) ? service.color : service.custom_color,
            icon: _.isNull(service.custom_icon) ? service.service_icon : service.custom_icon,
            is_connected: service.is_connected,
            is_custom_service: service.service_id >= 1000000,
            has_custom_icon: !_.isNull(service.custom_icon) && service.custom_icon !== 'default',
            has_live_integration: ['1', true, 1].includes(service.has_live_integration),
            has_stored_data: service.has_stored_data,
            child_of_service_id: service.child_of_service_id,
            connector_marketplace_url: service.connector_marketplace_url,
            live_assignments_threshold: service.live_assignments_threshold,
            mapbox_config: service.mapbox_config || [],
        };
    }

    function getServicesWithStoredData() {
        return _.filter(services, {has_stored_data: true});
    }

    /**
     * Allows read only access to the services
     * @param include_exclusive_live_services
     * @returns {{}}
     */
    function getAllServices(include_exclusive_live_services = false) {
        return _.assign({}, include_exclusive_live_services ? services : getServicesWithStoredData());
    }

    /**
     * Allows read only access to the connected services
     * @param include_exclusive_live_services
     * @returns {Array}
     */
    function getConnectedServices(include_exclusive_live_services = false) {
        return _.filter(include_exclusive_live_services ? services : getServicesWithStoredData() , {is_connected: true});
    }

    /**
     * Get a specific service by id
     * @param id
     * @returns {T|*}
     */
    function getService(id) {
        return _.find(services, {id: id});
    }

    /**
     * @param id
     * @returns {T|*}
     */
    function getFirstConnectedService() {
        return _.first(getConnectedServices());
    }

    function refreshServices(params = {}) {
        ServiceResource.getServices(params).then(function (data) {
            setServices(data);
        });
    }

    async function asyncRefreshServices(queryParams) {
        return new Promise(async (resolve, reject) => {
            try {
                const data = await ServiceResource.getServices(queryParams);
                setServices(data);
                resolve();
            } catch (e) {
                reject(e.message);
            }
        });
    }


    //
    // CATEGORIES /////////////////////////////////////
    //
    var categories = {};

    /**
     * Sets the categories var to be exposed externally
     * @param values
     * @returns {*}
     */
    function setCategories(values) {
        categories = _.map(values, function (category) {
            return {
                type: DataSourceType.CATEGORY_DATA,
                id: category.id,
                name: category.name,
                color: category.color,
                parent: category.parent,
                services: category.service_ids,
                icon: category.custom_icon || 'icomoon-bar-chart',
                is_connected: category.is_connected,
                is_predefined: category.is_predefined,
                has_custom_icon: !_.isNull(category.custom_icon),
                live_assignments_threshold: category.live_assignments_threshold,
            };
        });
    }

    /**
     * Allows read only access to the categories
     * @returns {{}}
     */
    function getAllCategories() {
        return _.assign({}, categories);
    }

    /**
     * Allows read only access to the connected categories
     * @returns {Array}
     */
    function getConnectedCategories() {
        return _.filter(categories, {is_connected: true});
    }

    /**
     * Get a specific category by id
     * @param id
     * @returns {T|*}
     */
    function getCategory(id) {
        return _.find(categories, {id: id});
    }

    function refreshCategories() {
        return CategoryFactory.getCategories().then(function (data) {
            setCategories(data);
        });
    }


    //
    // SEO WEBSITES /////////////////////////////////////
    //
    var seoWebsites = [];

    /**
     * Sets the seo websites var to be exposed externally
     * @param values
     * @returns {*}
     */
    function setSeoWebsites(values) {
        seoWebsites = _.map(values, function (seoWebsite) {
            return {
                type: DataSourceType.SEO_WEBSITES,
                id: seoWebsite.key.toString(),
                name: seoWebsite.name,
                url: seoWebsite.url,
                clientId: seoWebsite.client_id.toString()
            };
        });
    }

    /**
     * Allows read only access to the seo websites
     * @returns {Array}
     */
    function getSeoWebsites() {
        return seoWebsites;
    }

    //
    // DATE RANGES /////////////////////////////////////
    //
    var dateRange = {
        start: null,
        end: null,
        relativeRange: null
    };

    /**
     * Check if a date is a unix timestamp
     * @param date
     * @returns {boolean}
     */
    function checkIsUnix(date) {
        return !moment.isMoment(date) && moment.unix(date).isValid();
    }

    /**
     * Sets the dateRange var to be exposed externally (should only accept moment object)
     * @param start
     * @param end
     * @param relativeRange
     */
    function setDateRange(start, end, relativeRange) {
        if (!moment.unix(start).isValid()
            || !moment.unix(end).isValid()
        ) {
            console.warn('must use unix timestamp for AppFactory.setDateRange');
        }
        var startIsUnix = checkIsUnix(start);
        var endIsUnix = checkIsUnix(end);

        dateRange.formatted = {
            start: startIsUnix
                ? moment.unix(start).utc().format(LanguageService.getDisplayDateFormat())
                : moment(start).format(LanguageService.getDisplayDateFormat()),
            end: endIsUnix
                ? moment.unix(end).utc().format(LanguageService.getDisplayDateFormat())
                : moment(end).format(LanguageService.getDisplayDateFormat())
        };
        dateRange.start = startIsUnix ? start : moment.utc(start.format(MomentDateFormat.ISO)).unix();
        dateRange.end = endIsUnix ? end : moment.utc(end.format(MomentDateFormat.ISO)).unix();
        dateRange.relativeRange = relativeRange || RelativeDateRange.DEFAULT;

        PubSub.$emit($DateRangeEvents.DATE_RANGE_CHANGE);
    }

    /**
     * Allows read only access to the dateRange var
     * @returns {{}}
     */
    function getDateRange() {
        return _.assign({}, dateRange);
    }

    /**
     * Allows read only access to the dateRange var but
     * @returns {{}}
     */
    function getFormattedDateRange() {
        return _.assign({}, dateRange.formatted);
    }

    /**
     * Helper to get currently selected dashboard relative range
     * @returns {RelativeDateRange}
     */
    function getCurrentRelativeDateRange() {
        return dateRange.relativeRange;
    }

    /**
     * In a day, submit any unix timestamp it will return last second of the day 23:59:59 timestamp
     */
    function convertUnixTimestampToLastSecondOfDayInUTC(unixTimestamp) {
        const date = moment.unix(unixTimestamp);

        // Set the time to 23:59:59 (last second of the day) in UTC
        date.utc().set({ hour: 23, minute: 59, second: 59, millisecond: 999 });

        return date.unix();
    }

    //
    // COMPARISON DATE RANGE /////////////////////////////////////
    //
    var comparisonDateRange = {
        enabled: false,
        start: null,
        end: null
    };

    /**
     * Toggles Date Range Comparison
     */
    function toggleComparisonDateRange(boolean) {
        comparisonDateRange.enabled = boolean;
        PubSub.$emit($DateRangeEvents.COMPARISON_DATE_RANGE_CHANGE);
        return comparisonDateRange.enabled;
    }

    /**
     * Sets the dateRange var to be exposed externally (should only accept moment object)
     * @param start
     * @param end
     * @param enabled
     */
    function setComparisonDateRange(start, end, enabled) {
        if (!moment.unix(start).isValid()
            || !moment.unix(end).isValid()
        ) {
            console.warn('must use unix timestamp for AppFactory.setComparisonDateRange');
        }
        var startIsUnix = checkIsUnix(start);
        var endIsUnix = checkIsUnix(end);

        comparisonDateRange.formatted = {
            start: startIsUnix
                ? moment.utc(moment.unix(start)).format(LanguageService.getDisplayDateFormat())
                : moment(start).format(LanguageService.getDisplayDateFormat()),
            end: endIsUnix
                ? moment.utc(moment.unix(end)).format(LanguageService.getDisplayDateFormat())
                : moment(end).format(LanguageService.getDisplayDateFormat())
        };
        comparisonDateRange.start = startIsUnix ? start : moment.utc(start.format(MomentDateFormat.ISO)).unix();
        comparisonDateRange.end = endIsUnix ? end : moment.utc(end.format(MomentDateFormat.ISO)).unix();
        comparisonDateRange.enabled =  _.isUndefined(enabled) ? comparisonDateRange.enabled : enabled;

        PubSub.$emit($DateRangeEvents.COMPARISON_DATE_RANGE_CHANGE);
    }

    function getComparisonDateRange() {
        return _.assign({}, comparisonDateRange);
    }

    /**
     * Allows read only access to the comparisonDateRange var but
     * @returns {{}}
     */
    function getFormattedComparisonDateRange() {
        return _.assign({}, comparisonDateRange.formatted);
    }

    //
    // CLIENT + CLIENT GROUPS /////////////////////////////////////
    //

    /**
     * Sets the currentClient var to be exposed externally
     * @param values
     * @returns {*}
     */
    function setCurrentClient(id, name) {
        $.core.main.setCurrentClientGroup(null, null);
        $.core.main.setCurrentClient(id, name);
    }

    /**
     * Allows read only access to the dateRange var
     * @returns {{}}
     */
    function getCurrentClient() {
        return $.core.main.getCurrentClient();
    }

    /**
     * Sets the currentClientGroup var to be exposed externally
     * @param values
     * @returns {*}
     */
    function setCurrentClientGroup(id, name) {
        $.core.main.setCurrentClient(null, null);
        $.core.main.setCurrentClientGroup(id, name);
    }

    /**
     * Allows read only access to the dateRange var
     * @returns {{}}
     */
    function getCurrentClientGroup() {
        return $.core.main.getCurrentClientGroup();
    }

    /**
     * Sets the currentClientCurrency var to be exposed externally
     * @param values
     */
    function setCurrentClientCurrency(currencySymbol) {
        $.globals.utils.clientCurrencySymbol = currencySymbol;
    }

    /**
     * Allows read only access to the clientCurrency var
     * @returns {{}}
     */
    function getCurrentClientCurrency() {
        return $.globals.utils.clientCurrencySymbol;
    }

    /**
     * Sets the page title.
     * @param title
     */
    function setPageTitle(title) {
        const user = getUser();
        window.angularTitle = title;
        document.title = window.isNUI ? `${user.applicationName} - ${title}` : user.applicationName;
    }

    function canShowChatGptOption() {
        const currentUser = getUser();
        // module permission, new UI, chatgpt integration
        return currentUser.shouldUseNui() &&
            currentUser.isModuleAvailable(AppModule.AI_SPARKLE) &&
            Permission.hasPermissionToWrite(Permission.moduleName.DASHBOARDCHATGPTSUMMARIES);
    }

    function getDomainNameFromUrl(anchorString) {
        // Create a URL object using the extracted URL
        const url = new URL(anchorString[1]);
        // Return the hostname (domain name)
        return url.hostname;
    }

    return {
        util: util,
        // Angular general fns
        invalidateWatch: invalidateWatch,
        arrayToMemoizedObj: arrayToMemoizedObj,
        // User fns
        getUser: getUser,
        setUser: setUser,
        mustGoToNewApp: mustGoToNewApp,
        isUserModuleAvailable: isUserModuleAvailable,
        isUserFeatureAvailable: isUserFeatureAvailable,
        getInstanceTimezone: getInstanceTimezone,
        getMdsRoute: getMdsRoute,
        // Data Type fns
        setDataTypes: setDataTypes,
        getDataTypes: getDataTypes,
        getSelectableDataTypes: getSelectableDataTypes,
        getNumericDataTypes: getNumericDataTypes,
        getDataType: getDataType,
        dataURItoBlob: dataURItoBlob,
        // Service fns
        setDataSourceTypes: setDataSourceTypes,
        getDataSourceTypes: getDataSourceTypes,
        setServices: setServices,
        mapService: mapService,
        getAllServices: getAllServices,
        getConnectedServices: getConnectedServices,
        getService: getService,
        getFirstConnectedService: getFirstConnectedService,
        refreshServices: refreshServices,
        asyncRefreshServices: asyncRefreshServices,

        // Brand mapping fns
        setBrandMappings: setBrandMappings,
        getBrandMappings: getBrandMappings,
        // Category fns
        setCategories: setCategories,
        getAllCategories: getAllCategories,
        getConnectedCategories: getConnectedCategories,
        getSeoWebsites: getSeoWebsites,
        setSeoWebsites: setSeoWebsites,
        getCategory: getCategory,
        refreshCategories: refreshCategories,
        // Date range fns
        setDateRange: setDateRange,
        getDateRange: getDateRange,
        getFormattedDateRange: getFormattedDateRange,
        getCurrentRelativeDateRange: getCurrentRelativeDateRange,
        convertUnixTimestampToLastSecondOfDayInUTC: convertUnixTimestampToLastSecondOfDayInUTC,
        // Comparison Date fns
        toggleComparisonDateRange: toggleComparisonDateRange,
        setComparisonDateRange: setComparisonDateRange,
        getComparisonDateRange: getComparisonDateRange,
        getFormattedComparisonDateRange: getFormattedComparisonDateRange,
        // Client + client group fns
        setCurrentClient: setCurrentClient,
        getCurrentClient: getCurrentClient,
        setCurrentClientGroup: setCurrentClientGroup,
        getCurrentClientGroup: getCurrentClientGroup,
        setCurrentClientCurrency: setCurrentClientCurrency,
        getCurrentClientCurrency: getCurrentClientCurrency,

        setPageTitle: setPageTitle,
        canShowChatGptOption: canShowChatGptOption,
        getDomainNameFromUrl: getDomainNameFromUrl
    };
}

/**
 * App service
 * @ngInject
 */
function AppService($rootScope, $http, $templateCache) {
    return {
        submitForm: function(url, model, files) {
            return $http({
                method: 'POST',
                url: url,
                headers: { 'Content-Type': undefined },
                //This allows to convert the form data to support file uploads
                //It is then processed in BaseController (ln 163)
                transformRequest: function (data) {
                    var formData = new FormData();
                    formData.append('model', angular.toJson(data.model));
                    if (data.files != null) {
                        for (var i = 0; i < data.files.length; i++) {
                            formData.append(data.files[i].key, data.files[i]);
                        }
                    }
                    return formData;
                },
                data: { model: model, files: files }
            });
        },
        deleteRow: function(obj, $row, $modal, callback) {

            var notificationMessage = 'was successfully deleted';
            if (obj.data('termination-object') == 'user')
            {
                notificationMessage = 'was made inactive';
            }

            //Legacy delete
            if (!obj.data('isAngular')) {
                return $http.get(obj.data('url')).success(function (json) {
                    if (json.status == 'success' || json.error === false) {
                        var tableId = $row.closest('table.dataTable').attr('id');
                        $.core.datatable.oTables[tableId].oTable.api().row($row).remove().draw();
                        $.core.main.notify(notificationMessage, $.globals.notify.success, {title: obj.data('name') == '' ? 'Row' : obj.data('name')});

                        // callback action set in dataTable options
                        callback && callback();
                    }
                    else {
                        $.core.main.formErrorNotify(json);
                    }

                    $.core.main.hideModal($modal);

                    //For now, to ensure consistent data we clear the cache when saving an entity
                    //This will be fixed when porting views to angular 2 way binding methodology
                    $templateCache.removeAll();
                    initAngularTemplatedDirectives($templateCache);
                });
            }
            else {
                return $http.delete($rootScope.util.apiUrl(obj.data('url'))).success(function (json) {
                    if (!json.error) {
                        var tableId = $row.closest('table.dataTable').attr('id');
                        $.core.datatable.oTables[tableId].oTable.api().row($row).remove().draw();
                        $.core.main.notify(notificationMessage, $.globals.notify.success, {title: obj.data('name') == '' ? 'Row' : obj.data('name')});

                        // callback action set in dataTable options
                        callback && callback();
                    }
                    else {
                        $.core.main.formErrorNotify(json);
                    }

                    $.core.main.hideModal($modal);
                });
            }
        },
        downloadCsv: function(url, params) {
            return $.fileDownload(url, {
                httpMethod: 'POST',
                data: params,
                successCallback: function (url) {
                    $.core.main.hideAjaxMainLoading();
                },
                failCallback: function (html, url) {
                    $.core.main.hideAjaxMainLoading();

                    const errData = JSON.parse(html.replace(/<[^>]*>?/gm, ''));
                    let errMsg = 'Download failed';
                    if (errData) {
                        errMsg += ': ' + errData.data[0];
                    }
                    $.core.main.notify(errMsg, $.globals.notify.error);
                }
            });
        },
        getReportingProfiles: function(cluster_id) {
            return $http({
                method: 'POST',
                url: $.fn.actionUrl('reportingProfile/get_cluster_filtered_reporting_profiles'),
                data: { 'cluster_id': cluster_id}
            });
        },
        getClusters: function() {
            return $http.post($.fn.actionUrl('cluster/get_cluster_list'));
        },
        //Used for getting asset url in assets/custom (needs to be done server side since in includes instanceServerName)
        getAssetUrl: function(assetName, ext) {
            return $http.post($rootScope.util.mageUrl('api/getLogoUrl'), {asset_name: assetName, ext: (ext == null ? 'png' : ext)});
        },
        sendPerformanceReport: function(data) {
            return $http.post($rootScope.util.apiUrl('performance_report'), data);
        }
    }
}

/**
 * Function that allows us to recover the templates cached for angular directives
 * NOTE: this is only needed while we are still using $cacheTemplate.removeAll()
 */
function initAngularTemplatedDirectives($templateCache) {

    // rz-slider
    $templateCache.put("rzSliderTpl.html", "<span class=rz-bar-wrapper><span class=rz-bar></span></span> <span class=rz-bar-wrapper><span class=\"rz-bar rz-selection\" ng-style=barStyle></span></span> <span class=\"rz-pointer rz-pointer-min\" ng-style=minPointerStyle></span> <span class=\"rz-pointer rz-pointer-max\" ng-style=maxPointerStyle></span> <span class=\"rz-bubble rz-limit\"></span> <span class=\"rz-bubble rz-limit\"></span> <span class=rz-bubble></span> <span class=rz-bubble></span> <span class=rz-bubble></span><ul ng-show=showTicks class=rz-ticks><li ng-repeat=\"t in ticks track by $index\" class=rz-tick ng-class=\"{'rz-selected': t.selected}\" ng-style=t.style ng-attr-uib-tooltip=\"{{ t.tooltip }}\" ng-attr-tooltip-placement={{t.tooltipPlacement}} ng-attr-tooltip-append-to-body=\"{{ t.tooltip ? true : undefined}}\"><span ng-if=\"t.value != null\" class=rz-tick-value ng-attr-uib-tooltip=\"{{ t.valueTooltip }}\" ng-attr-tooltip-placement={{t.valueTooltipPlacement}}>{{ t.value }}</span> <span ng-if=\"t.legend != null\" class=rz-tick-legend>{{ t.legend }}</span></li></ul>");
    $templateCache.put("template/tooltip/tooltip-popup.html",
        "<div class=\"tooltip\"\n" +
        "  tooltip-animation-class=\"fade\"\n" +
        "  tooltip-classes\n" +
        "  ng-class=\"{ in: isOpen() }\">\n" +
        "  <div class=\"tooltip-arrow\"></div>\n" +
        "  <div class=\"tooltip-inner\" ng-bind=\"content\"></div>\n" +
        "</div>\n" +
        "");
    $templateCache.put("template/pagination/pagination.html",
        "<ul class=\"pagination\">\n" +
        "  <li ng-if=\"::boundaryLinks\" ng-class=\"{disabled: noPrevious()||ngDisabled}\" class=\"pagination-first\"><a href ng-click=\"selectPage(1, $event)\">{{::getText('first')}}</a></li>\n" +
        "  <li ng-if=\"::directionLinks\" ng-class=\"{disabled: noPrevious()||ngDisabled}\" class=\"pagination-prev\"><a href ng-click=\"selectPage(page - 1, $event)\">{{::getText('previous')}}</a></li>\n" +
        "  <li ng-repeat=\"page in pages track by $index\" ng-class=\"{active: page.active,disabled: ngDisabled&&!page.active}\" class=\"pagination-page\"><a href ng-click=\"selectPage(page.number, $event)\">{{page.text}}</a></li>\n" +
        "  <li ng-if=\"::directionLinks\" ng-class=\"{disabled: noNext()||ngDisabled}\" class=\"pagination-next\"><a href ng-click=\"selectPage(page + 1, $event)\">{{::getText('next')}}</a></li>\n" +
        "  <li ng-if=\"::boundaryLinks\" ng-class=\"{disabled: noNext()||ngDisabled}\" class=\"pagination-last\"><a href ng-click=\"selectPage(totalPages, $event)\">{{::getText('last')}}</a></li>\n" +
        "</ul>\n" +
        "");
    $templateCache.put("select2/select.tpl.html", "<div class=\"ui-select-container select2 select2-container\" ng-class=\"{\'select2-container-active select2-dropdown-open open\': $select.open, \'select2-container-disabled\': $select.disabled, \'select2-container-active\': $select.focus, \'select2-allowclear\': $select.allowClear && !$select.isEmpty()}\"><div class=\"ui-select-match\"></div><div class=\"ui-select-dropdown select2-drop select2-with-searchbox select2-drop-active\" ng-class=\"{\'select2-display-none\': !$select.open}\"><div class=\"select2-search\" ng-show=\"$select.searchEnabled\"><input type=\"text\" autocomplete=\"false\" autocorrect=\"false\" autocapitalize=\"off\" spellcheck=\"false\" role=\"combobox\" aria-expanded=\"true\" aria-owns=\"ui-select-choices-{{ $select.generatedId }}\" aria-label=\"{{ $select.baseTitle }}\" aria-activedescendant=\"ui-select-choices-row-{{ $select.generatedId }}-{{ $select.activeIndex }}\" class=\"ui-select-search select2-input\" ng-model=\"$select.search\"></div><div class=\"ui-select-choices\"></div></div></div>");
    $templateCache.put("select2/match.tpl.html", "<a class=\"select2-choice ui-select-match\" ng-class=\"{\'select2-default\': $select.isEmpty()}\" ng-click=\"$select.toggle($event)\" aria-label=\"{{ $select.baseTitle }} select\"><span ng-show=\"$select.isEmpty()\" class=\"select2-chosen\">{{$select.placeholder}}</span> <span ng-hide=\"$select.isEmpty()\" class=\"select2-chosen\" ng-transclude=\"\"></span> <abbr ng-if=\"$select.allowClear && !$select.isEmpty()\" class=\"select2-search-choice-close\" ng-click=\"$select.clear($event)\"></abbr> <span class=\"select2-arrow ui-select-toggle\"><b></b></span></a>");
    $templateCache.put("select2/choices.tpl.html", "<ul class=\"ui-select-choices ui-select-choices-content select2-results\"><li class=\"ui-select-choices-group\" ng-class=\"{\'select2-result-with-children\': $select.choiceGrouped($group) }\"><div ng-show=\"$select.choiceGrouped($group)\" class=\"ui-select-choices-group-label select2-result-label\" ng-bind=\"$group.name\"></div><ul role=\"listbox\" id=\"ui-select-choices-{{ $select.generatedId }}\" ng-class=\"{\'select2-result-sub\': $select.choiceGrouped($group), \'select2-result-single\': !$select.choiceGrouped($group) }\"><li role=\"option\" id=\"ui-select-choices-row-{{ $select.generatedId }}-{{$index}}\" class=\"ui-select-choices-row\" ng-class=\"{\'select2-highlighted\': $select.isActive(this), \'select2-disabled\': $select.isDisabled(this)}\"><div class=\"select2-result-label ui-select-choices-row-inner\"></div></li></ul></li></ul>");
    $templateCache.put("bootstrap/select-multiple.tpl.html", "<div class=\"ui-select-container ui-select-multiple ui-select-bootstrap dropdown form-control\" ng-class=\"{open: $select.open}\"><div><div class=\"ui-select-match\"></div><input type=\"text\" autocomplete=\"false\" autocorrect=\"off\" autocapitalize=\"off\" spellcheck=\"false\" class=\"ui-select-search input-xs\" placeholder=\"{{$selectMultiple.getPlaceholder()}}\" ng-disabled=\"$select.disabled\" ng-hide=\"$select.disabled\" ng-click=\"$select.activate()\" ng-model=\"$select.search\" role=\"combobox\" aria-label=\"{{ $select.baseTitle }}\" ondrop=\"return false;\"></div><div class=\"ui-select-choices\"></div></div>");
    $templateCache.put("bootstrap/choices.tpl.html", "<ul class=\"ui-select-choices ui-select-choices-content ui-select-dropdown dropdown-menu\" role=\"listbox\" ng-show=\"$select.items.length > 0\"><li class=\"ui-select-choices-group\" id=\"ui-select-choices-{{ $select.generatedId }}\"><div class=\"divider\" ng-show=\"$select.isGrouped && $index > 0\"></div><div ng-show=\"$select.isGrouped\" class=\"ui-select-choices-group-label dropdown-header\" ng-bind=\"$group.name\"></div><div id=\"ui-select-choices-row-{{ $select.generatedId }}-{{$index}}\" class=\"ui-select-choices-row\" ng-class=\"{active: $select.isActive(this), disabled: $select.isDisabled(this)}\" role=\"option\"><a href=\"\" class=\"ui-select-choices-row-inner\"></a></div></li></ul>");
    $templateCache.put("bootstrap/match-multiple.tpl.html", "<span class=\"ui-select-match\"><span ng-repeat=\"$item in $select.selected\"><span class=\"ui-select-match-item btn btn-default btn-xs\" tabindex=\"-1\" type=\"button\" ng-disabled=\"$select.disabled\" ng-click=\"$selectMultiple.activeMatchIndex = $index;\" ng-class=\"{\'btn-primary\':$selectMultiple.activeMatchIndex === $index, \'select-locked\':$select.isLocked(this, $index)}\" ui-select-sort=\"$select.selected\"><span class=\"close ui-select-match-close\" ng-hide=\"$select.disabled\" ng-click=\"$selectMultiple.removeChoice($index)\">&nbsp;&times;</span> <span uis-transclude-append=\"\"></span></span></span></span>");
    $templateCache.put("template/modal/backdrop.html",
        "<div class=\"modal-backdrop\"\n" +
        "     modal-animation-class=\"fade\"\n" +
        "     modal-in-class=\"in\"\n" +
        "     ng-style=\"{'z-index': 1040 + (index && 1 || 0) + index*10}\"\n" +
        "></div>\n" +
        "");
    $templateCache.put("template/modal/window.html",
        "<div modal-render=\"{{$isRendered}}\" tabindex=\"-1\" role=\"dialog\" class=\"modal\"\n" +
        "    modal-animation-class=\"fade\"\n" +
        "    modal-in-class=\"in\"\n" +
        "	ng-style=\"{'z-index': 1050 + index*10, display: 'block'}\" ng-click=\"close($event)\">\n" +
        "    <div class=\"modal-dialog\" ng-class=\"size ? 'modal-' + size : ''\"><div class=\"modal-content\" modal-transclude></div></div>\n" +
        "</div>\n" +
        "");
    $templateCache.put("template/datepicker/datepicker.html",
        "<div ng-switch=\"datepickerMode\" role=\"application\" ng-keydown=\"keydown($event)\">\n" +
        "  <daypicker ng-switch-when=\"day\" tabindex=\"0\"></daypicker>\n" +
        "  <monthpicker ng-switch-when=\"month\" tabindex=\"0\"></monthpicker>\n" +
        "  <yearpicker ng-switch-when=\"year\" tabindex=\"0\"></yearpicker>\n" +
        "</div>");
    $templateCache.put("template/datepicker/popup.html",
        "<ul class=\"dropdown-menu\" ng-if=\"isOpen\" style=\"display: block\" ng-style=\"{top: position.top+'px', left: position.left+'px'}\" ng-keydown=\"keydown($event)\" ng-click=\"$event.stopPropagation()\">\n" +
        "	<li ng-transclude></li>\n" +
        "	<li ng-if=\"showButtonBar\" style=\"padding:10px 9px 2px\">\n" +
        "		<span class=\"btn-group pull-left\">\n" +
        "			<button type=\"button\" class=\"btn btn-sm btn-info\" ng-click=\"select('today')\">{{ getText('current') }}</button>\n" +
        "			<button type=\"button\" class=\"btn btn-sm btn-danger\" ng-click=\"select(null)\">{{ getText('clear') }}</button>\n" +
        "		</span>\n" +
        "		<button type=\"button\" class=\"btn btn-sm btn-success pull-right\" ng-click=\"close()\">{{ getText('close') }}</button>\n" +
        "	</li>\n" +
        "</ul>\n" +
        "");
    $templateCache.put("template/datepicker/day.html",
        "<table role=\"grid\" aria-labelledby=\"{{::uniqueId}}-title\" aria-activedescendant=\"{{activeDateId}}\">\n" +
        "  <thead>\n" +
        "    <tr>\n" +
        "      <th><button type=\"button\" class=\"btn btn-default btn-sm pull-left\" ng-click=\"move(-1)\" tabindex=\"-1\"><i class=\"glyphicon glyphicon-chevron-left\"></i></button></th>\n" +
        "      <th colspan=\"{{::5 + showWeeks}}\"><button id=\"{{::uniqueId}}-title\" role=\"heading\" aria-live=\"assertive\" aria-atomic=\"true\" type=\"button\" class=\"btn btn-default btn-sm\" ng-click=\"toggleMode()\" ng-disabled=\"datepickerMode === maxMode\" tabindex=\"-1\" style=\"width:100%;\"><strong>{{title}}</strong></button></th>\n" +
        "      <th><button type=\"button\" class=\"btn btn-default btn-sm pull-right\" ng-click=\"move(1)\" tabindex=\"-1\"><i class=\"glyphicon glyphicon-chevron-right\"></i></button></th>\n" +
        "    </tr>\n" +
        "    <tr>\n" +
        "      <th ng-if=\"showWeeks\" class=\"text-center\"></th>\n" +
        "      <th ng-repeat=\"label in ::labels track by $index\" class=\"text-center\"><small aria-label=\"{{::label.full}}\">{{::label.abbr}}</small></th>\n" +
        "    </tr>\n" +
        "  </thead>\n" +
        "  <tbody>\n" +
        "    <tr ng-repeat=\"row in rows track by $index\">\n" +
        "      <td ng-if=\"showWeeks\" class=\"text-center h6\"><em>{{ weekNumbers[$index] }}</em></td>\n" +
        "      <td ng-repeat=\"dt in row track by dt.date\" class=\"text-center\" role=\"gridcell\" id=\"{{::dt.uid}}\" ng-class=\"::dt.customClass\">\n" +
        "        <button type=\"button\" style=\"min-width:100%;\" class=\"btn btn-default btn-sm\" ng-class=\"{'btn-info': dt.selected, active: isActive(dt)}\" ng-click=\"select(dt.date)\" ng-disabled=\"dt.disabled\" tabindex=\"-1\"><span ng-class=\"::{'text-muted': dt.secondary, 'text-info': dt.current}\">{{::dt.label}}</span></button>\n" +
        "      </td>\n" +
        "    </tr>\n" +
        "  </tbody>\n" +
        "</table>\n" +
        "");
    $templateCache.put("template/datepicker/month.html",
        "<table role=\"grid\" aria-labelledby=\"{{::uniqueId}}-title\" aria-activedescendant=\"{{activeDateId}}\">\n" +
        "  <thead>\n" +
        "    <tr>\n" +
        "      <th><button type=\"button\" class=\"btn btn-default btn-sm pull-left\" ng-click=\"move(-1)\" tabindex=\"-1\"><i class=\"glyphicon glyphicon-chevron-left\"></i></button></th>\n" +
        "      <th><button id=\"{{::uniqueId}}-title\" role=\"heading\" aria-live=\"assertive\" aria-atomic=\"true\" type=\"button\" class=\"btn btn-default btn-sm\" ng-click=\"toggleMode()\" ng-disabled=\"datepickerMode === maxMode\" tabindex=\"-1\" style=\"width:100%;\"><strong>{{title}}</strong></button></th>\n" +
        "      <th><button type=\"button\" class=\"btn btn-default btn-sm pull-right\" ng-click=\"move(1)\" tabindex=\"-1\"><i class=\"glyphicon glyphicon-chevron-right\"></i></button></th>\n" +
        "    </tr>\n" +
        "  </thead>\n" +
        "  <tbody>\n" +
        "    <tr ng-repeat=\"row in rows track by $index\">\n" +
        "      <td ng-repeat=\"dt in row track by dt.date\" class=\"text-center\" role=\"gridcell\" id=\"{{::dt.uid}}\" ng-class=\"::dt.customClass\">\n" +
        "        <button type=\"button\" style=\"min-width:100%;\" class=\"btn btn-default\" ng-class=\"{'btn-info': dt.selected, active: isActive(dt)}\" ng-click=\"select(dt.date)\" ng-disabled=\"dt.disabled\" tabindex=\"-1\"><span ng-class=\"::{'text-info': dt.current}\">{{::dt.label}}</span></button>\n" +
        "      </td>\n" +
        "    </tr>\n" +
        "  </tbody>\n" +
        "</table>\n" +
        "");
    $templateCache.put("template/datepicker/year.html",
        "<table role=\"grid\" aria-labelledby=\"{{::uniqueId}}-title\" aria-activedescendant=\"{{activeDateId}}\">\n" +
        "  <thead>\n" +
        "    <tr>\n" +
        "      <th><button type=\"button\" class=\"btn btn-default btn-sm pull-left\" ng-click=\"move(-1)\" tabindex=\"-1\"><i class=\"glyphicon glyphicon-chevron-left\"></i></button></th>\n" +
        "      <th colspan=\"3\"><button id=\"{{::uniqueId}}-title\" role=\"heading\" aria-live=\"assertive\" aria-atomic=\"true\" type=\"button\" class=\"btn btn-default btn-sm\" ng-click=\"toggleMode()\" ng-disabled=\"datepickerMode === maxMode\" tabindex=\"-1\" style=\"width:100%;\"><strong>{{title}}</strong></button></th>\n" +
        "      <th><button type=\"button\" class=\"btn btn-default btn-sm pull-right\" ng-click=\"move(1)\" tabindex=\"-1\"><i class=\"glyphicon glyphicon-chevron-right\"></i></button></th>\n" +
        "    </tr>\n" +
        "  </thead>\n" +
        "  <tbody>\n" +
        "    <tr ng-repeat=\"row in rows track by $index\">\n" +
        "      <td ng-repeat=\"dt in row track by dt.date\" class=\"text-center\" role=\"gridcell\" id=\"{{::dt.uid}}\">\n" +
        "        <button type=\"button\" style=\"min-width:100%;\" class=\"btn btn-default\" ng-class=\"{'btn-info': dt.selected, active: isActive(dt)}\" ng-click=\"select(dt.date)\" ng-disabled=\"dt.disabled\" tabindex=\"-1\"><span ng-class=\"::{'text-info': dt.current}\">{{::dt.label}}</span></button>\n" +
        "      </td>\n" +
        "    </tr>\n" +
        "  </tbody>\n" +
        "</table>\n" +
        "");
    $templateCache.put("template/tabs/tab.html",
        "<li ng-class=\"{active: active, disabled: disabled}\">\n" +
        "  <a href ng-click=\"select()\" tab-heading-transclude>{{heading}}</a>\n" +
        "</li>\n" +
        "");
    $templateCache.put("template/tabs/tabset.html",
        "<div>\n" +
        "  <ul class=\"nav nav-{{type || 'tabs'}}\" ng-class=\"{'nav-stacked': vertical, 'nav-justified': justified}\" ng-transclude></ul>\n" +
        "  <div class=\"tab-content\">\n" +
        "    <div class=\"tab-pane\" \n" +
        "         ng-repeat=\"tab in tabs\" \n" +
        "         ng-class=\"{active: tab.active}\"\n" +
        "         tab-content-transclude=\"tab\">\n" +
        "    </div>\n" +
        "  </div>\n" +
        "</div>\n" +
        "");
    $templateCache.put("template/timepicker/timepicker.html",
        "<table>\n" +
        "  <tbody>\n" +
        "    <tr class=\"text-center\" ng-show=\"::showSpinners\">\n" +
        "      <td><a ng-click=\"incrementHours()\" ng-class=\"{disabled: noIncrementHours()}\" class=\"btn btn-link\"><span class=\"glyphicon glyphicon-chevron-up\"></span></a></td>\n" +
        "      <td>&nbsp;</td>\n" +
        "      <td><a ng-click=\"incrementMinutes()\" ng-class=\"{disabled: noIncrementMinutes()}\" class=\"btn btn-link\"><span class=\"glyphicon glyphicon-chevron-up\"></span></a></td>\n" +
        "      <td ng-show=\"showMeridian\"></td>\n" +
        "    </tr>\n" +
        "    <tr>\n" +
        "      <td class=\"form-group\" ng-class=\"{'has-error': invalidHours}\">\n" +
        "        <input style=\"width:50px;\" type=\"text\" ng-model=\"hours\" ng-change=\"updateHours()\" class=\"form-control text-center\" ng-readonly=\"::readonlyInput\" maxlength=\"2\">\n" +
        "      </td>\n" +
        "      <td>:</td>\n" +
        "      <td class=\"form-group\" ng-class=\"{'has-error': invalidMinutes}\">\n" +
        "        <input style=\"width:50px;\" type=\"text\" ng-model=\"minutes\" ng-change=\"updateMinutes()\" class=\"form-control text-center\" ng-readonly=\"::readonlyInput\" maxlength=\"2\">\n" +
        "      </td>\n" +
        "      <td ng-show=\"showMeridian\"><button type=\"button\" ng-class=\"{disabled: noToggleMeridian()}\" class=\"btn btn-default text-center\" ng-click=\"toggleMeridian()\">{{meridian}}</button></td>\n" +
        "    </tr>\n" +
        "    <tr class=\"text-center\" ng-show=\"::showSpinners\">\n" +
        "      <td><a ng-click=\"decrementHours()\" ng-class=\"{disabled: noDecrementHours()}\" class=\"btn btn-link\"><span class=\"glyphicon glyphicon-chevron-down\"></span></a></td>\n" +
        "      <td>&nbsp;</td>\n" +
        "      <td><a ng-click=\"decrementMinutes()\" ng-class=\"{disabled: noDecrementMinutes()}\" class=\"btn btn-link\"><span class=\"glyphicon glyphicon-chevron-down\"></span></a></td>\n" +
        "      <td ng-show=\"showMeridian\"></td>\n" +
        "    </tr>\n" +
        "  </tbody>\n" +
        "</table>\n" +
        "");
    $templateCache.put("directives/decorators/bootstrap/radio-buttons.html", "<div class=\"form-group schema-form-radiobuttons {{form.htmlClass}}\" ng-class=\"{\'has-error\': form.disableErrorState !== true && hasError(), \'has-success\': form.disableSuccessState !== true && hasSuccess()}\"><div><label class=\"control-label {{form.labelHtmlClass}}\" ng-show=\"showTitle()\">{{form.title}}</label></div><div class=\"btn-group\"><label class=\"btn {{ (item.value === $$value$$) ? form.style.selected || \'btn-default\' : form.style.unselected || \'btn-default\'; }}\" ng-class=\"{ active: item.value === $$value$$ }\" ng-repeat=\"item in form.titleMap\"><input type=\"radio\" class=\"{{form.fieldHtmlClass}}\" sf-changed=\"form\" style=\"display: none;\" ng-disabled=\"form.readonly\" ng-model=\"$$value$$\" ng-model-options=\"form.ngModelOptions\" schema-validate=\"form\" ng-value=\"item.value\" name=\"{{form.key.join(\'.\')}}\"> <span ng-bind-html=\"item.name\"></span></label></div><div class=\"help-block\" sf-message=\"form.description\"></div> <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div></div>");
    $templateCache.put("directives/decorators/bootstrap/radios-inline.html", "<div class=\"form-group schema-form-radios-inline {{form.htmlClass}}\" ng-class=\"{\'has-error\': form.disableErrorState !== true && hasError(), \'has-success\': form.disableSuccessState !== true && hasSuccess()}\"><label class=\"control-label {{form.labelHtmlClass}}\" ng-show=\"showTitle()\">{{form.title}}</label><div><label class=\"radio-inline\" ng-repeat=\"item in form.titleMap\"><input type=\"radio\" class=\"{{form.fieldHtmlClass}}\" sf-changed=\"form\" ng-disabled=\"form.readonly\" ng-model=\"$$value$$\" schema-validate=\"form\" ng-value=\"item.value\" name=\"{{form.key.join(\'.\')}}\"> <span ng-bind-html=\"item.name\"></span></label></div><div class=\"help-block\" sf-message=\"form.description\"></div> <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div></div>");
    $templateCache.put("directives/decorators/bootstrap/radios.html", "<div class=\"form-group schema-form-radios {{form.htmlClass}}\" ng-class=\"{\'has-error\': form.disableErrorState !== true && hasError(), \'has-success\': form.disableSuccessState !== true && hasSuccess()}\"><label class=\"control-label {{form.labelHtmlClass}}\" ng-show=\"showTitle()\">{{form.title}}</label><div class=\"radio\" ng-repeat=\"item in form.titleMap\"><label><input type=\"radio\" class=\"{{form.fieldHtmlClass}}\" sf-changed=\"form\" ng-disabled=\"form.readonly\" ng-model=\"$$value$$\" ng-model-options=\"form.ngModelOptions\" schema-validate=\"form\" ng-value=\"item.value\" name=\"{{form.key.join(\'.\')}}\"> <span ng-bind-html=\"item.name\"></span></label></div><div class=\"help-block\" sf-message=\"form.description\"></div> <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div></div>");
    $templateCache.put("directives/decorators/bootstrap/submit.html", "<div class=\"form-group schema-form-submit {{form.htmlClass}}\"><input type=\"submit\" class=\"btn {{ form.style || \'btn-primary\' }} {{form.fieldHtmlClass}}\" value=\"{{form.title}}\" ng-disabled=\"form.readonly\" ng-if=\"form.type === \'submit\'\"> <button class=\"btn submit-width {{ form.style || \'btn-default\' }}\" type=\"button\" ng-click=\"buttonClick($event,form)\" ng-disabled=\"form.readonly || form.isSubmitted\" ng-if=\"form.type !== \'submit\'\"><span ng-if=\"form.icon\" class=\"{{form.icon}}\"></span> {{form.title}}</button></div>");
    $templateCache.put("directives/decorators/bootstrap/default.html", "<div class=\"schema-form-{{form.type}} {{form.htmlClass}}\" ng-class=\"{\'has-error\': form.disableErrorState !== true && hasError(), \'has-success\': form.disableSuccessState !== true && hasSuccess(), \'has-feedback\': form.feedback !== false }\"> <div class=\"form-group\"><label class=\"control-label {{form.labelHtmlClass}}\" ng-class=\"{\'sr-only\': !showTitle()}\" for=\"{{form.key.slice(-1)[0]}}\">{{form.title}}</label> <div class=\"{{form.fieldHtmlClass}}\"><input ng-if=\"!form.fieldAddonLeft && !form.fieldAddonRight\" ng-show=\"form.key\" type=\"{{form.type}}\" step=\"any\" sf-changed=\"form\" placeholder=\"{{form.placeholder}}\" class=\"form-control field-width\" id=\"{{form.key.slice(-1)[0]}}\" ng-model-options=\"form.ngModelOptions\" ng-model=\"$$value$$\" ng-disabled=\"form.readonly\" schema-validate=\"form\" name=\"{{form.key.slice(-1)[0]}}\" aria-describedby=\"{{form.key.slice(-1)[0] + \'Status\'}}\"><div class=\"help-block\" sf-message=\"form.description\"></div> <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div></div><div ng-if=\"form.fieldAddonLeft || form.fieldAddonRight\" ng-class=\"{\'input-group\': (form.fieldAddonLeft || form.fieldAddonRight)}\"><span ng-if=\"form.fieldAddonLeft\" class=\"input-group-addon\" ng-bind-html=\"form.fieldAddonLeft\"></span> <input ng-show=\"form.key\" type=\"{{form.type}}\" step=\"any\" sf-changed=\"form\" placeholder=\"{{form.placeholder}}\" class=\"form-control {{form.fieldHtmlClass}}\" id=\"{{form.key.slice(-1)[0]}}\" ng-model-options=\"form.ngModelOptions\" ng-model=\"$$value$$\" ng-disabled=\"form.readonly\" schema-validate=\"form\" name=\"{{form.key.slice(-1)[0]}}\" aria-describedby=\"{{form.key.slice(-1)[0] + \'Status\'}}\"> <span ng-if=\"form.fieldAddonRight\" class=\"input-group-addon\" ng-bind-html=\"form.fieldAddonRight\"></span><div class=\"help-block\" sf-message=\"form.description\"></div> <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div><span ng-if=\"form.feedback !== false\" class=\"form-control-feedback\" ng-class=\"evalInScope(form.feedback) || {\'glyphicon\': true, \'glyphicon-ok\': hasSuccess(), \'glyphicon-remove\': hasError() }\" aria-hidden=\"true\"></span> <span ng-if=\"hasError() || hasSuccess()\" id=\"{{form.key.slice(-1)[0] + \'Status\'}}\" class=\"sr-only\">{{ hasSuccess() ? \'(success)\' : \'(error)\' }}</span></div></div></div>");
    $templateCache.put("directives/decorators/bootstrap/section.html", "<div class=\"{{form.containerClass}}\"><div class=\"schema-form-section {{form.htmlClass}}\"><div ng-if=\"form.sectionTitle\" class=\"main-content-title\"><p class=\"main-title\">{{form.sectionTitle}}</p></div><sf-decorator ng-repeat=\"item in form.items\" form=\"item\"></sf-decorator></div></div>");
    $templateCache.put("directives/decorators/bootstrap/select.html", "<div class=\"{{form.htmlClass}} schema-form-select\" ng-class=\"{\'has-error\': form.disableErrorState !== true && hasError(), \'has-success\': form.disableSuccessState !== true && hasSuccess(), \'has-feedback\': form.feedback !== false}\"><div class=\"form-group\"><label class=\"control-label {{form.labelHtmlClass}}\" ng-show=\"showTitle()\">{{form.title}}</label><div class=\"{{form.fieldHtmlClass}}\"><select ng-model=\"$$value$$\" ng-model-options=\"form.ngModelOptions\" ng-disabled=\"form.readonly\" sf-changed=\"form\" class=\"form-control\" ng-class=\"item.showLongText ? 'field-width-value' : 'no-field-value' \" schema-validate=\"form\" ng-options=\"item.value as item.name group by item.group for item in form.titleMap\" name=\"{{form.key.slice(-1)[0]}}\"></select><label class=\"selected-dropdown-value\" ng-show=\"item.showLongText && $$value$$\">Selected Value: <span>{{$$value$$}}</span></label><div class=\"help-block\" sf-message=\"form.description\"></div> <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div></div></div></div>");
    $templateCache.put("directives/decorators/bootstrap/textarea.html", "<div class=\"has-feedback {{form.htmlClass}} schema-form-textarea\" ng-class=\"{\'has-error\': form.disableErrorState !== true && hasError(), \'has-success\': form.disableSuccessState !== true && hasSuccess()}\"><div class=\"form-group\"><label class=\"control-label {{form.labelHtmlClass}}\" ng-class=\"{\'sr-only\': !showTitle()}\" for=\"{{form.key.slice(-1)[0]}}\">{{form.title}}</label><div class=\"{{form.fieldHtmlClass}}\"> <textarea ng-if=\"!form.fieldAddonLeft && !form.fieldAddonRight\" class=\"form-control\" id=\"{{form.key.slice(-1)[0]}}\" sf-changed=\"form\" placeholder=\"{{form.placeholder}}\" ng-disabled=\"form.readonly\" ng-model=\"$$value$$\" ng-model-options=\"form.ngModelOptions\" schema-validate=\"form\" name=\"{{form.key.slice(-1)[0]}}\"></textarea><span class=\"help-block\" sf-message=\"form.description\"></span> <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div></div></div></div>");
    $templateCache.put("directives/decorators/bootstrap/checkbox.html", "<div class=\"schema-form-checkbox {{form.htmlClass}}\" ng-class=\"{\'has-error\': form.disableErrorState !== true && hasError(), \'has-success\': form.disableSuccessState !== true && hasSuccess()}\"><div class=\"form-group\"><label class=\" control-label {{form.labelHtmlClass}}\">{{form.title}}</label><div class=\"{{form.fieldHtmlClass}}\"><input type=\"checkbox\" sf-changed=\"form\" ng-disabled=\"form.readonly\" ng-model=\"$$value$$\" ng-model-options=\"form.ngModelOptions\" schema-validate=\"form\" name=\"{{form.key.slice(-1)[0]}}\"><div class=\"help-block\" sf-message=\"form.description\"></div> <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div> </div></div></div>");
    $templateCache.put("directives/decorators/bootstrap/uiselect/single.html", "<div class=\"{{form.htmlClass}}\" ng-class=\"{\'has-error\': hasError(), \'has-success\': hasSuccess(), \'has-feedback\': form.feedback !== false}\" ng-init=\"select_models=(form.schema.items | where : {value: $$value$$})\">\n <div class=\"form-group\"> <label class=\"control-label {{form.labelHtmlClass}}\" ng-show=\"showTitle()\">{{form.title}}</label>\n  <div class=\"{{form.fieldHtmlClass}}\" ng-init=\"select_model.selected=select_models[0]\">\n    <ui-select ng-model=\"select_model.selected\" ng-controller=\"UiSelectController\"  ng-if=\"!(form.options.tagging||false)\" theme=\"bootstrap\" ng-disabled=\"form.disabled\" on-select=\"$$value$$=$item.value\" class=\"{{form.options.uiClass}}\">\n      <ui-select-match placeholder=\"{{form.placeholder || form.schema.placeholder || (\'placeholders.select\' | translate)}}\">{{select_model.selected.label}}</ui-select-match>\n      <ui-select-choices refresh=\"fetchResult(form.schema, form.options, $select)\"\n             refresh-delay=\"form.options.refreshDelay\" group-by=\"form.options.groupBy\"  repeat=\"item in form.schema.items | propsFilter: {label: $select.search, description: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\') }\">\n                <div ng-bind-html=\"item.label | highlight: $select.search\"></div>\n      <div ng-if=\"item.description\"> \n        <span ng-bind-html=\"\'<small>\' + (\'\'+item.description | highlight: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\'))+ \'</small>\'\"></span></div>\n      </ui-select-choices>\n    </ui-select>\n    <ui-select ng-controller=\"UiSelectController\"  ng-model=\"select_model.selected\" ng-if=\"(form.options.tagging||false) && !(form.options.groupBy || false)\" tagging=\"form.options.tagging||false\" tagging-label=\"form.options.taggingLabel\" tagging-tokens=\"form.options.taggingTokens\"\n      theme=\"bootstrap\" ng-disabled=\"form.disabled\" on-select=\"$$value$$=$item.value\" class=\"{{form.options.uiClass}}\">\n      <ui-select-match placeholder=\"{{form.placeholder || form.schema.placeholder || (\'placeholders.select\' | translate)}}\">{{select_model.selected.label}}&nbsp;<small>{{(select_model.selected.isTag===true ? form.options.taggingLabel : \'\')}}</small></ui-select-match>\n      <!--repeat code because tagging does not display properly under group by but is still useful -->\n      <ui-select-choices  refresh=\"form.options.refreshMethod(form.schema, $select.search)\"\n             refresh-delay=\"form.options.refreshDelay\" repeat=\"item in form.schema.items | propsFilter: {label: $select.search, description: (form.options.searchDescription===true ? $select.search : \'NOTSEARCHINGFORTHIS\') }\">\n        <div ng-if=\"item.isTag\" ng-bind-html=\"\'<div>\' + (item.label   | highlight: $select.search) + \' \' + form.options.taggingLabel + \'</div><div class=&quot;divider&quot;></div>\'\"></div><div ng-if=\"!item.isTag\" ng-bind-html=\"item.label + item.isTag| highlight: $select.search\"></div>\n      <div ng-if=\"item.description\"> \n        <span  ng-bind-html=\"\'<small>\' + (\'\'+item.description | highlight: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\')) + \'</small>\'\"></span>\n      </div>\n    </ui-select-choices>\n    </ui-select>\n\n      <!--repeat code because tagging does not display properly under group by but is still useful -->\n\n    <ui-select ng-controller=\"UiSelectController\"  ng-model=\"select_model.selected\" ng-if=\"(form.options.tagging||false) && (form.options.groupBy || false)\"  tagging=\"form.options.tagging||false\" tagging-label=\"form.options.taggingLabel\" tagging-tokens=\"form.options.taggingTokens\"\n      theme=\"bootstrap\" ng-disabled=\"form.disabled\" on-select=\"$$value$$=$item.value\" class=\"{{form.options.uiClass}}\">\n      <ui-select-match placeholder=\"{{form.placeholder || form.schema.placeholder || (\'placeholders.select\' | translate)}}\">{{select_model.selected.label}}&nbsp;<small>{{(select_model.selected.isTag===true ? form.options.taggingLabel : \'\')}}</small></ui-select-match>\n      <ui-select-choices group-by=\"form.options.groupBy\" refresh=\"form.options.refreshMethod(form.schema, $select.search)\"\n             refresh-delay=\"form.options.refreshDelay\" repeat=\"item in form.schema.items | propsFilter: {label: $select.search, description: (form.options.searchDescription===true ? $select.search : \'NOTSEARCHINGFORTHIS\') }\">\n        <div ng-if=\"item.isTag\" ng-bind-html=\"\'<div>\' + (item.label   | highlight: $select.search) + \' \' + form.options.taggingLabel + \'</div><div class=&quot;divider&quot;></div>\'\"></div><div ng-if=\"!item.isTag\" ng-bind-html=\"item.label + item.isTag| highlight: $select.search\"></div>\n      <div ng-if=\"item.description\"> \n        <span  ng-bind-html=\"\'<small>\' + (\'\'+item.description | highlight: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\')) + \'</small>\'\"></span>\n      </div>\n    </ui-select-choices>\n    </ui-select>\n    <input type=\"hidden\" toggle-single-model sf-changed=\"form\" ng-model=\"insideModel\" schema-validate=\"form\" />\n    <span ng-if=\"form.feedback !== false\"\n      class=\"form-control-feedback\"\n      ng-class=\"evalInScope(form.feedback) || {\'glyphicon\': true, \'glyphicon-ok\': hasSuccess(), \'glyphicon-remove\': hasError() }\"></span>\n    <div class=\"help-block\"\n ng-show=\"(hasError() && errorMessage(schemaError())) || form.description\"\n  ng-bind-html=\"(hasError() && errorMessage(schemaError())) || form.description\"></div>\n <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div> </div>\n </div>\n</div>\n");
    $templateCache.put("directives/decorators/bootstrap/uiselect/multi.html", "<div class=\"{{form.htmlClass}}\" ng-class=\"{\'has-error\': hasError(), \'has-success\': hasSuccess(), \'has-feedback\': form.feedback !== false}\" ng-init=\"form.select_models=(form.schema.items| whereMulti : \'value\' : ($$value$$||[]))\">\n <div class=\"form-group\"> <label class=\"control-label {{form.labelHtmlClass}}\" ng-show=\"showTitle()\">{{form.title}}</label>\n  <div class=\"{{form.fieldHtmlClass}}\" ng-controller=\"UiSelectController\">\n    <ui-select ui-select-no-animate ng-disabled=\"form.readonly===true\" tagging=\"tagHandler\" multiple close-on-select=\"false\" sortable-options=\"{{form.sortableOptions}}\" ng-if=\"!(form.options.tagging||false)\" array-required=\"{{form.isRequired}}\" ng-model=\"form.select_models\" theme=\"bootstrap\" on-select=\"$$value$$.push($item.value)\" on-remove=\"$$value$$.splice($$value$$.indexOf($item.value), 1)\" class=\"field-width {{form.options.uiClass}}\">\n      <ui-select-match placeholder=\"{{form.placeholder || form.schema.placeholder || (\'placeholders.select\' | translate)}}\">{{$item.label}}</ui-select-match>\n      <ui-select-choices refresh=\"fetchResult(form.schema, form.options, $select)\"\n         refresh-delay=\"form.options.refreshDelay\" group-by=\"form.options.groupBy\"  repeat=\"item in form.schema.items | propsFilter: {label: $select.search, description: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\') }\">\n        <div ng-bind-html=\"item.label | highlight: $select.search\"></div>\n        <div ng-if=\"item.description\">\n          <span ng-bind-html=\"\'<small>\' + (\'\'+item.description | highlight: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\'))+ \'</small>\'\"></span>\n        </div>\n    </ui-select-choices>\n    </ui-select>\n    <ui-select ng-controller=\"UiSelectController\" multiple ng-if=\"(form.options.tagging||false) && !(form.options.groupBy || false)\" tagging=\"form.options.tagging||false\" tagging-label=\"form.options.taggingLabel\" tagging-tokens=\"form.options.taggingTokens\" sortable-options=\"{{form.sortableOptions}}\" array-required=\"{{form.isRequired}}\" ng-model=\"form.select_models\" theme=\"bootstrap\" on-select=\"$$value$$.push($item.value)\" on-remove=\"$$value$$.splice($$value$$.indexOf($item.value), 1)\" class=\"{{form.options.uiClass}}\">\n      <ui-select-match placeholder=\"{{form.placeholder || form.schema.placeholder || (\'placeholders.select\' | translate)}}\">{{$item.label}}&nbsp;<small>{{($item.isTag===true ?  form.options.taggingLabel : \'\')}}</small></ui-select-match>\n      <ui-select-choices  refresh-delay=\"form.options.refreshDelay\" refresh=\"fetchResult(form.schema, form.options, $select)\"\n         refresh-delay=\"form.options.refreshDelay\" repeat=\"item in form.schema.items | propsFilter: {label: $select.search, description: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\') }\">\n          <div ng-if=\"item.isTag\" ng-bind-html=\"\'<div>\' + (item.label   | highlight: $select.search) + \' \' + form.options.taggingLabel + \'</div><div class=&quot;divider&quot;></div>\'\"></div>\n          <div ng-if=\"!item.isTag\" ng-bind-html=\"item.label + item.isTag | highlight: $select.search\"></div>\n      <div ng-if=\"item.description\"> \n        <span ng-bind-html=\"\'<small>\' + (\'\'+item.description | highlight: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\')) + \'</small>\'\"></span>\n      </div>\n    </ui-select-choices>\n      <!--repeat code because tagging does not display properly under group by but is still useful -->\n    </ui-select>\n\n    <ui-select ng-controller=\"UiSelectController\" multiple ng-if=\"(form.options.tagging||false) && (form.options.groupBy || false)\" tagging=\"form.options.tagging||false\" tagging-label=\"form.options.taggingLabel\" tagging-tokens=\"form.options.taggingTokens\" sortable-options=\"{{form.sortableOptions}}\" array-required=\"{{form.isRequired}}\" ng-model=\"form.select_models\" theme=\"bootstrap\" on-select=\"$$value$$.push($item.value)\" on-remove=\"$$value$$.splice($$value$$.indexOf($item.value), 1)\" class=\"{{form.options.uiClass}}\">\n      <ui-select-match placeholder=\"{{form.placeholder || form.schema.placeholder || (\'placeholders.select\' | translate)}}\">{{$item.label}}&nbsp;<small>{{($item.isTag===true ?  form.options.taggingLabel : \'\')}}</small></ui-select-match>\n      <ui-select-choices group-by=\"form.options.groupBy\" refresh-delay=\"form.options.refreshDelay\" refresh=\"fetchResult(form.schema, form.options, $select)\"\n         refresh-delay=\"form.options.refreshDelay\" repeat=\"item in form.schema.items | propsFilter: {label: $select.search, description: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\') }\">\n          <div ng-if=\"item.isTag\" ng-bind-html=\"\'<div>\' + (item.label   | highlight: $select.search) + \' \' + form.options.taggingLabel + \'</div><div class=&quot;divider&quot;></div>\'\"></div>\n          <div ng-if=\"!item.isTag\" ng-bind-html=\"item.label + item.isTag | highlight: $select.search\"></div>\n      <div ng-if=\"item.description\"> \n        <span ng-bind-html=\"\'<small>\' + (\'\'+item.description | highlight: (form.options.searchDescriptions===true ? $select.search : \'NOTSEARCHINGFORTHIS\')) + \'</small>\'\"></span>\n      </div>\n    </ui-select-choices>\n\n    </ui-select>\n    <input toggle-model type=\"hidden\" ng-model=\"insideModel\" sf-changed=\"form\" schema-validate=\"form\" />\n    <span ng-if=\"form.feedback !== false\"\n      class=\"form-control-feedback\"\n      ng-class=\"evalInScope(form.feedback) || {\'glyphicon\': true, \'glyphicon-ok\': hasSuccess(), \'glyphicon-remove\': hasError() }\"></span>\n    <div class=\"help-block\"\n      ng-show=\"(hasError() && errorMessage(schemaError())) || form.description\"\n      ng-bind-html=\"(hasError() && errorMessage(schemaError())) || form.description\"></div>\n <div class=\"notice-block help-content\" ng-bind-html=\"form.helpText\"></div> </div>\n</div>\n");
    $templateCache.put("mwl/calendar.html", "<div\n  class=\"cal-context\"\n  ng-switch=\"vm.view\"\n  ng-if=\"vm.templatesLoaded\">\n\n  <div class=\"alert alert-danger\" ng-switch-default>The value passed to the view attribute of the calendar is not set</div>\n\n  <div class=\"alert alert-danger\" ng-hide=\"vm.viewDate\">The value passed to view-date attribute of the calendar is not set</div>\n\n  <mwl-calendar-year\n    events=\"vm.events\"\n    view-date=\"vm.viewDate\"\n    on-event-click=\"vm.onEventClick\"\n    on-event-times-changed=\"vm.onEventTimesChanged\"\n    on-timespan-click=\"vm.onTimespanClick\"\n    cell-is-open=\"vm.cellIsOpen\"\n    cell-modifier=\"vm.cellModifier\"\n    slide-box-disabled=\"vm.slideBoxDisabled\"\n    custom-template-urls=\"vm.customTemplateUrls\"\n    template-scope=\"vm.templateScope\"\n    ng-switch-when=\"year\">\n  </mwl-calendar-year>\n\n  <mwl-calendar-month\n    events=\"vm.events\"\n    view-date=\"vm.viewDate\"\n    on-event-click=\"vm.onEventClick\"\n    on-event-times-changed=\"vm.onEventTimesChanged\"\n    on-timespan-click=\"vm.onTimespanClick\"\n    on-date-range-select=\"vm.onDateRangeSelect\"\n    cell-is-open=\"vm.cellIsOpen\"\n    cell-modifier=\"vm.cellModifier\"\n    slide-box-disabled=\"vm.slideBoxDisabled\"\n    custom-template-urls=\"vm.customTemplateUrls\"\n    template-scope=\"vm.templateScope\"\n    ng-switch-when=\"month\">\n  </mwl-calendar-month>\n\n  <mwl-calendar-week\n    events=\"vm.events\"\n    view-date=\"vm.viewDate\"\n    on-event-click=\"vm.onEventClick\"\n    on-event-times-changed=\"vm.onEventTimesChanged\"\n    day-view-start=\"vm.dayViewStart\"\n    day-view-end=\"vm.dayViewEnd\"\n    day-view-split=\"vm.dayViewSplit\"\n    day-view-event-chunk-size=\"vm.dayViewEventChunkSize\"\n    on-timespan-click=\"vm.onTimespanClick\"\n    on-date-range-select=\"vm.onDateRangeSelect\"\n    custom-template-urls=\"vm.customTemplateUrls\"\n    cell-modifier=\"vm.cellModifier\"\n    template-scope=\"vm.templateScope\"\n    ng-switch-when=\"week\">\n  </mwl-calendar-week>\n\n  <mwl-calendar-day\n    events=\"vm.events\"\n    view-date=\"vm.viewDate\"\n    on-event-click=\"vm.onEventClick\"\n    on-event-times-changed=\"vm.onEventTimesChanged\"\n    on-timespan-click=\"vm.onTimespanClick\"\n    on-date-range-select=\"vm.onDateRangeSelect\"\n    day-view-start=\"vm.dayViewStart\"\n    day-view-end=\"vm.dayViewEnd\"\n    day-view-split=\"vm.dayViewSplit\"\n    day-view-event-chunk-size=\"vm.dayViewEventChunkSize\"\n    custom-template-urls=\"vm.customTemplateUrls\"\n    cell-modifier=\"vm.cellModifier\"\n    template-scope=\"vm.templateScope\"\n    ng-switch-when=\"day\">\n  </mwl-calendar-day>\n</div>\n");
    $templateCache.put("mwl/calendarDayView.html", "<div class=\"cal-week-box cal-all-day-events-box\" ng-if=\"vm.allDayEvents.length > 0\">\n  <div class=\"cal-day-panel clearfix\">\n    <div class=\"row\">\n      <div class=\"col-xs-12\">\n        <div class=\"cal-row-fluid\">\n          <div\n            class=\"cal-cell-6 day-highlight\"\n            ng-style=\"{backgroundColor: event.color.secondary}\"\n            data-event-class\n            ng-repeat=\"event in vm.allDayEvents track by event.$id\">\n            <strong>\n              <span ng-bind=\"event.startsAt | calendarDate:'datetime':true\"></span>\n              <span ng-if=\"event.endsAt\">\n                - <span ng-bind=\"event.endsAt | calendarDate:'datetime':true\"></span>\n              </span>\n            </strong>\n            <a\n              href=\"javascript:;\"\n              class=\"event-item\"\n              ng-bind-html=\"vm.calendarEventTitle.dayView(event) | calendarTrustAsHtml\">\n            </a>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"cal-day-box\">\n  <div class=\"cal-day-panel clearfix\" ng-style=\"{height: vm.dayViewHeight + 'px', minWidth: vm.viewWidth + 'px'}\">\n\n    <mwl-calendar-hour-list\n      day-view-start=\"vm.dayViewStart\"\n      day-view-end=\"vm.dayViewEnd\"\n      day-view-split=\"vm.dayViewSplit\"\n      on-timespan-click=\"vm.onTimespanClick\"\n      on-date-range-select=\"vm.onDateRangeSelect\"\n      on-event-times-changed=\"vm.onEventTimesChanged\"\n      view-date=\"vm.viewDate\"\n      custom-template-urls=\"vm.customTemplateUrls\"\n      template-scope=\"vm.templateScope\"\n      cell-modifier=\"vm.cellModifier\">\n    </mwl-calendar-hour-list>\n\n    <div\n      class=\"pull-left day-event day-highlight\"\n      ng-repeat=\"dayEvent in vm.nonAllDayEvents track by dayEvent.event.$id\"\n      ng-class=\"dayEvent.event.cssClass\"\n      ng-style=\"{\n        top: dayEvent.top - 1 + 'px',\n        left: dayEvent.left + 60 + 'px',\n        height: dayEvent.height + 'px',\n        backgroundColor: dayEvent.event.color.secondary,\n        borderColor: dayEvent.event.color.primary\n      }\"\n      mwl-draggable=\"dayEvent.event.draggable === true\"\n      axis=\"'xy'\"\n      snap-grid=\"{y: vm.dayViewEventChunkSize || 30, x: 50}\"\n      on-drag=\"vm.eventDragged(dayEvent.event, y / 30)\"\n      on-drag-end=\"vm.eventDragComplete(dayEvent.event, y / 30)\"\n      mwl-resizable=\"dayEvent.event.resizable === true && dayEvent.event.endsAt\"\n      resize-edges=\"{top: true, bottom: true}\"\n      on-resize=\"vm.eventResized(dayEvent.event, edge, y / 30)\"\n      on-resize-end=\"vm.eventResizeComplete(dayEvent.event, edge, y / 30)\">\n\n      <span class=\"cal-hours\">\n        <span ng-show=\"dayEvent.top == 0\"><span ng-bind=\"(dayEvent.event.tempStartsAt || dayEvent.event.startsAt) | calendarDate:'day':true\"></span>, </span>\n        <span ng-bind=\"(dayEvent.event.tempStartsAt || dayEvent.event.startsAt) | calendarDate:'time':true\"></span>\n      </span>\n      <a href=\"javascript:;\" class=\"event-item\" ng-click=\"vm.onEventClick({calendarEvent: dayEvent.event})\">\n        <span ng-bind-html=\"vm.calendarEventTitle.dayView(dayEvent.event) | calendarTrustAsHtml\"></span>\n      </a>\n\n      <a\n        href=\"javascript:;\"\n        class=\"event-item-action\"\n        ng-repeat=\"action in dayEvent.event.actions track by $index\"\n        ng-class=\"action.cssClass\"\n        ng-bind-html=\"action.label | calendarTrustAsHtml\"\n        ng-click=\"action.onClick({calendarEvent: dayEvent.event})\">\n      </a>\n\n    </div>\n\n  </div>\n\n</div>\n");
    $templateCache.put("mwl/calendarHourList.html", "<div class=\"cal-day-panel-hour\">\n\n  <div class=\"cal-day-hour\" ng-repeat=\"hour in vm.hourGrid track by $index\">\n\n    <div\n      class=\"cal-day-hour-part\"\n      ng-repeat=\"segment in hour.segments track by $index\"\n      ng-class=\"[{ 'cal-day-hour-part-selected': vm.dateRangeSelect &&\n                vm.dateRangeSelect.startDate <= vm.getClickedDate(segment.date, vm.dayViewSplit * $index) &&\n                vm.getClickedDate(segment.date, vm.dayViewSplit * $index) < vm.dateRangeSelect.endDate }, segment.cssClass]\"\n      ng-click=\"vm.onTimespanClick({calendarDate: vm.getClickedDate(segment.date, vm.dayViewSplit * $index)})\"\n      mwl-droppable\n      on-drop=\"vm.eventDropped(dropData.event, vm.getClickedDate(segment.date, vm.dayViewSplit * $index))\"\n      mwl-drag-select=\"!!vm.onDateRangeSelect\"\n      on-drag-select-start=\"vm.onDragSelectStart(vm.getClickedDate(segment.date, vm.dayViewSplit * $index))\"\n      on-drag-select-move=\"vm.onDragSelectMove(vm.getClickedDate(segment.date, vm.dayViewSplit * ($index + 1)))\"\n      on-drag-select-end=\"vm.onDragSelectEnd(vm.getClickedDate(segment.date, vm.dayViewSplit * ($index + 1)))\"\n      ng-if=\"!vm.dayWidth\">\n      <div class=\"cal-day-hour-part-time\">\n        <strong ng-bind=\"segment.date | calendarDate:'hour':true\" ng-if=\"segment.isStart\"></strong>\n      </div>\n    </div>\n\n    <div\n      class=\"cal-day-hour-part\"\n      ng-repeat=\"segment in hour.segments track by $index\"\n      ng-if=\"vm.dayWidth\">\n      <div class=\"cal-day-hour-part-time\">\n        <strong ng-bind=\"segment.date | calendarDate:'hour':true\" ng-if=\"segment.isStart\"></strong>\n        &nbsp;\n      </div>\n      <div\n        class=\"cal-day-hour-part-spacer\"\n        ng-repeat=\"dayIndex in [0, 1, 2, 3, 4, 5, 6]\"\n        ng-style=\"{width: vm.dayWidth + 'px'}\"\n        ng-class=\"[{ 'cal-day-hour-part-selected': vm.dateRangeSelect &&\n                vm.dateRangeSelect.startDate <= vm.getClickedDate(segment.date, vm.dayViewSplit * $parent.$index, dayIndex) &&\n                vm.getClickedDate(segment.date, vm.dayViewSplit * $parent.$index, dayIndex) < vm.dateRangeSelect.endDate }, segment.cssClass]\"\n        ng-click=\"vm.onTimespanClick({calendarDate: vm.getClickedDate(segment.date, vm.dayViewSplit * $parent.$index, dayIndex)})\"\n        mwl-droppable\n        on-drop=\"vm.eventDropped(dropData.event, vm.getClickedDate(segment.date, vm.dayViewSplit * $parent.$index, dayIndex))\"\n        mwl-drag-select=\"!!vm.onDateRangeSelect\"\n        on-drag-select-start=\"vm.onDragSelectStart(vm.getClickedDate(segment.date, vm.dayViewSplit * $parent.$index, dayIndex), dayIndex)\"\n        on-drag-select-move=\"vm.onDragSelectMove(vm.getClickedDate(segment.date, vm.dayViewSplit * ($parent.$index + 1), vm.dateRangeSelect.dayIndex))\"\n        on-drag-select-end=\"vm.onDragSelectEnd(vm.getClickedDate(segment.date, vm.dayViewSplit * ($parent.$index + 1), vm.dateRangeSelect.dayIndex))\">\n      </div>\n    </div>\n\n  </div>\n\n</div>\n");
    $templateCache.put("mwl/calendarMonthCell.html", "<div\n  mwl-droppable\n  on-drop=\"vm.handleEventDrop(dropData.event, day.date, dropData.draggedFromDate)\"\n  mwl-drag-select=\"!!vm.onDateRangeSelect\"\n  on-drag-select-start=\"vm.onDragSelectStart(day)\"\n  on-drag-select-move=\"vm.onDragSelectMove(day)\"\n  on-drag-select-end=\"vm.onDragSelectEnd(day)\"\n  class=\"cal-month-day {{ day.cssClass }}\"\n  ng-class=\"{\n    'cal-day-outmonth': !day.inMonth,\n    'cal-day-inmonth': day.inMonth,\n    'cal-day-weekend': day.isWeekend,\n    'cal-day-past': day.isPast,\n    'cal-day-today': day.isToday,\n    'cal-day-future': day.isFuture,\n    'cal-day-selected': vm.dateRangeSelect && vm.dateRangeSelect.startDate <= day.date && day.date <= vm.dateRangeSelect.endDate\n  }\">\n\n  <small\n    class=\"cal-events-num badge badge-important pull-left\"\n    ng-show=\"day.badgeTotal > 0\"\n    ng-bind=\"day.badgeTotal\">\n  </small>\n\n  <span\n    class=\"pull-right\"\n    data-cal-date\n    ng-click=\"vm.calendarCtrl.dateClicked(day.date)\"\n    ng-bind=\"day.label\">\n  </span>\n\n  <div class=\"cal-day-tick\" ng-show=\"dayIndex === vm.openDayIndex && vm.view[vm.openDayIndex].events.length > 0 && !vm.slideBoxDisabled\">\n    <i class=\"glyphicon glyphicon-chevron-up\"></i>\n    <i class=\"fa fa-chevron-up\"></i>\n  </div>\n\n  <ng-include src=\"vm.customTemplateUrls.calendarMonthCellEvents || vm.calendarConfig.templates.calendarMonthCellEvents\"></ng-include>\n\n  <div id=\"cal-week-box\" ng-if=\"$first && rowHovered\">\n    <span ng-bind=\"vm.getWeekNumberLabel(day)\"></span>\n  </div>\n\n</div>\n");
    $templateCache.put("mwl/calendarMonthCellEvents.html", "<div class=\"events-list\" ng-show=\"day.events.length > 0\">\n  <a\n    ng-repeat=\"event in day.events | orderBy:'startsAt' track by event.$id\"\n    href=\"javascript:;\"\n    ng-click=\"$event.stopPropagation(); vm.onEventClick({calendarEvent: event})\"\n    class=\"pull-left event\"\n    ng-class=\"event.cssClass\"\n    ng-style=\"{backgroundColor: event.color.primary}\"\n    ng-mouseenter=\"vm.highlightEvent(event, true)\"\n    ng-mouseleave=\"vm.highlightEvent(event, false)\"\n    tooltip-append-to-body=\"true\"\n    uib-tooltip-html=\"vm.calendarEventTitle.monthViewTooltip(event) | calendarTrustAsHtml\"\n    mwl-draggable=\"event.draggable === true\"\n    drop-data=\"{event: event, draggedFromDate: day.date.toDate()}\">\n  </a>\n</div>\n");
    $templateCache.put("mwl/calendarMonthView.html", "<div class=\"cal-row-fluid cal-row-head\">\n\n  <div class=\"cal-cell1\" ng-repeat=\"day in vm.weekDays track by $index\" ng-bind=\"day\"></div>\n\n</div>\n<div class=\"cal-month-box\">\n\n  <div\n    ng-repeat=\"rowOffset in vm.monthOffsets track by rowOffset\"\n    ng-mouseenter=\"rowHovered = true\"\n    ng-mouseleave=\"rowHovered = false\">\n    <div class=\"cal-row-fluid cal-before-eventlist\">\n      <div\n        ng-repeat=\"day in vm.view | calendarLimitTo:7:rowOffset track by $index\"\n        ng-init=\"dayIndex = vm.view.indexOf(day)\"\n        class=\"cal-cell1 cal-cell {{ day.highlightClass }}\"\n        ng-style=\"{backgroundColor: day.backgroundColor}\"\n        ng-click=\"vm.dayClicked(day, false, $event)\"\n        ng-class=\"{pointer: day.events.length > 0}\">\n        <ng-include src=\"vm.customTemplateUrls.calendarMonthCell || vm.calendarConfig.templates.calendarMonthCell\"></ng-include>\n      </div>\n    </div>\n\n    <mwl-calendar-slide-box\n      is-open=\"vm.openRowIndex === $index && vm.view[vm.openDayIndex].events.length > 0 && !vm.slideBoxDisabled\"\n      events=\"vm.view[vm.openDayIndex].events\"\n      on-event-click=\"vm.onEventClick\"\n      cell=\"vm.view[vm.openDayIndex]\"\n      custom-template-urls=\"vm.customTemplateUrls\"\n      template-scope=\"vm.templateScope\">\n    </mwl-calendar-slide-box>\n\n  </div>\n\n</div>\n");
    $templateCache.put("mwl/calendarSlideBox.html", "<div class=\"cal-slide-box\" uib-collapse=\"vm.isCollapsed\" mwl-collapse-fallback=\"vm.isCollapsed\">\n  <div class=\"cal-slide-content cal-event-list\">\n    <ul class=\"unstyled list-unstyled\">\n\n      <li\n        ng-repeat=\"event in vm.events | orderBy:'startsAt' track by event.$id\"\n        ng-class=\"event.cssClass\"\n        mwl-draggable=\"event.draggable === true\"\n        drop-data=\"{event: event}\">\n        <span class=\"pull-left event\" ng-style=\"{backgroundColor: event.color.primary}\"></span>\n        &nbsp;\n        <a\n          href=\"javascript:;\"\n          class=\"event-item\"\n          ng-click=\"vm.onEventClick({calendarEvent: event})\">\n          <span ng-bind-html=\"isMonthView ? vm.calendarEventTitle.monthView(event) : vm.calendarEventTitle.yearView(event) | calendarTrustAsHtml\"></span>\n        </a>\n\n        <a\n          href=\"javascript:;\"\n          class=\"event-item-action\"\n          ng-class=\"action.cssClass\"\n          ng-repeat=\"action in event.actions track by $index\"\n          ng-bind-html=\"action.label | calendarTrustAsHtml\"\n          ng-click=\"action.onClick({calendarEvent: event})\">\n        </a>\n\n      </li>\n\n    </ul>\n  </div>\n</div>\n");
    $templateCache.put("mwl/calendarWeekView.html", "<div class=\"cal-week-box\" ng-class=\"{'cal-day-box': vm.showTimes}\">\n  <div class=\"cal-row-fluid cal-row-head\">\n\n    <div\n      class=\"cal-cell1\"\n      ng-repeat=\"day in vm.view.days track by $index\"\n      ng-class=\"{\n        'cal-day-weekend': day.isWeekend,\n        'cal-day-past': day.isPast,\n        'cal-day-today': day.isToday,\n        'cal-day-future': day.isFuture}\"\n      mwl-element-dimensions=\"vm.dayColumnDimensions\"\n      mwl-droppable\n      on-drop=\"vm.eventDropped(dropData.event, day.date)\">\n\n      <span ng-bind=\"day.weekDayLabel\"></span>\n      <br>\n      <small>\n        <span\n          data-cal-date\n          ng-click=\"vm.calendarCtrl.dateClicked(day.date)\"\n          class=\"pointer\"\n          ng-bind=\"day.dayLabel\">\n        </span>\n      </small>\n\n    </div>\n\n  </div>\n\n  <div class=\"cal-day-panel clearfix\" ng-style=\"{height: vm.showTimes ? (vm.dayViewHeight + 'px') : 'auto'}\">\n\n    <mwl-calendar-hour-list\n      day-view-start=\"vm.dayViewStart\"\n      day-view-end=\"vm.dayViewEnd\"\n      day-view-split=\"vm.dayViewSplit\"\n      day-width=\"vm.dayColumnDimensions.width\"\n      view-date=\"vm.viewDate\"\n      on-timespan-click=\"vm.onTimespanClick\"\n      on-date-range-select=\"vm.onDateRangeSelect\"\n      custom-template-urls=\"vm.customTemplateUrls\"\n      cell-modifier=\"vm.cellModifier\"\n      template-scope=\"vm.templateScope\"\n      ng-if=\"vm.showTimes\">\n    </mwl-calendar-hour-list>\n\n    <div class=\"row\" ng-repeat=\"row in vm.view.eventRows track by $index\">\n      <div class=\"col-xs-12\">\n        <div class=\"cal-row-fluid\">\n          <div\n            ng-repeat=\"eventRow in row.row track by eventRow.event.$id\"\n            ng-class=\"'cal-cell' + (vm.showTimes ? 1 : eventRow.span) + (vm.showTimes ? '' : ' cal-offset' + eventRow.offset)\"\n            ng-style=\"{\n              top: vm.showTimes ? ((eventRow.top) + 'px') : 'auto',\n              position: vm.showTimes ? 'absolute' : 'inherit',\n              width: vm.showTimes ? (vm.dayColumnDimensions.width + 'px') : '',\n              left: vm.showTimes ? (vm.dayColumnDimensions.width * eventRow.offset) + 15 + 'px' : ''\n            }\">\n            <div\n              class=\"day-highlight\"\n              ng-class=\"[eventRow.event.cssClass, !vm.showTimes && eventRow.startsBeforeWeek ? '' : 'border-left-rounded', !vm.showTimes && eventRow.endsAfterWeek ? '' : 'border-right-rounded']\"\n              ng-style=\"{backgroundColor: eventRow.event.color.secondary}\"\n              data-event-class\n              mwl-draggable=\"eventRow.event.draggable === true\"\n              axis=\"vm.showTimes ? 'xy' : 'x'\"\n              snap-grid=\"vm.showTimes ? {x: vm.dayColumnDimensions.width, y: vm.dayViewEventChunkSize || 30} : {x: vm.dayColumnDimensions.width}\"\n              on-drag=\"vm.tempTimeChanged(eventRow.event, y / 30)\"\n              on-drag-end=\"vm.weekDragged(eventRow.event, x / vm.dayColumnDimensions.width, y / 30)\"\n              mwl-resizable=\"eventRow.event.resizable === true && eventRow.event.endsAt && !vm.showTimes\"\n              resize-edges=\"{left: true, right: true}\"\n              on-resize-end=\"vm.weekResized(eventRow.event, edge, x / vm.dayColumnDimensions.width)\">\n              <strong ng-bind=\"(eventRow.event.tempStartsAt || eventRow.event.startsAt) | calendarDate:'time':true\" ng-show=\"vm.showTimes\"></strong>\n              <a\n                href=\"javascript:;\"\n                ng-click=\"vm.onEventClick({calendarEvent: eventRow.event})\"\n                class=\"event-item\"\n                ng-bind-html=\"vm.calendarEventTitle.weekView(eventRow.event) | calendarTrustAsHtml\"\n                uib-tooltip-html=\"vm.calendarEventTitle.weekViewTooltip(eventRow.event) | calendarTrustAsHtml\"\n                tooltip-placement=\"left\"\n                tooltip-append-to-body=\"true\">\n              </a>\n            </div>\n          </div>\n        </div>\n      </div>\n\n    </div>\n\n  </div>\n</div>\n");
    $templateCache.put("mwl/calendarYearView.html", "<div class=\"cal-year-box\">\n  <div ng-repeat=\"rowOffset in [0, 4, 8] track by rowOffset\">\n    <div class=\"row cal-before-eventlist\">\n      <div\n        class=\"span3 col-md-3 col-xs-6 cal-cell {{ day.cssClass }}\"\n        ng-repeat=\"month in vm.view | calendarLimitTo:4:rowOffset track by $index\"\n        ng-init=\"monthIndex = vm.view.indexOf(month)\"\n        ng-click=\"vm.monthClicked(month, false, $event)\"\n        ng-class=\"{pointer: month.events.length > 0, 'cal-day-today': month.isToday}\"\n        mwl-droppable\n        on-drop=\"vm.handleEventDrop(dropData.event, month.date)\">\n\n        <span\n          class=\"pull-right\"\n          data-cal-date\n          ng-click=\"vm.calendarCtrl.dateClicked(month.date)\"\n          ng-bind=\"month.label\">\n        </span>\n\n        <small\n          class=\"cal-events-num badge badge-important pull-left\"\n          ng-show=\"month.badgeTotal > 0\"\n          ng-bind=\"month.badgeTotal\">\n        </small>\n\n        <div\n          class=\"cal-day-tick\"\n          ng-show=\"monthIndex === vm.openMonthIndex && vm.view[vm.openMonthIndex].events.length > 0 && !vm.slideBoxDisabled\">\n          <i class=\"glyphicon glyphicon-chevron-up\"></i>\n          <i class=\"fa fa-chevron-up\"></i>\n        </div>\n\n      </div>\n    </div>\n\n    <mwl-calendar-slide-box\n      is-open=\"vm.openRowIndex === $index && vm.view[vm.openMonthIndex].events.length > 0 && !vm.slideBoxDisabled\"\n      events=\"vm.view[vm.openMonthIndex].events\"\n      on-event-click=\"vm.onEventClick\"\n      cell=\"vm.view[vm.openMonthIndex]\"\n      custom-template-urls=\"vm.customTemplateUrls\"\n      template-scope=\"vm.templateScope\">\n    </mwl-calendar-slide-box>\n\n  </div>\n\n</div>\n");
    $templateCache.put("template/progressbar/bar.html","<div class=\"progress-bar\" ng-class=\"type && 'progress-bar-' + type\" role=\"progressbar\" aria-valuenow=\"{{value}}\" aria-valuemin=\"0\" aria-valuemax=\"{{max}}\" ng-style=\"{width: (percent < 100 ? percent : 100) + '%'}\" aria-valuetext=\"{{percent | number:0}}%\" style=\"min-width: 0;\" ng-transclude></div>\n" +
        "");
    $templateCache.put("template/progressbar/progress.html","<div class=\"progress\" ng-transclude></div>");
    $templateCache.put("template/progressbar/progressbar.html",
        "<div class=\"progress\">\n" +
        "  <div class=\"progress-bar\" ng-class=\"type && 'progress-bar-' + type\" role=\"progressbar\" aria-valuenow=\"{{value}}\" aria-valuemin=\"0\" aria-valuemax=\"{{max}}\" ng-style=\"{width: (percent < 100 ? percent : 100) + '%'}\" aria-valuetext=\"{{percent | number:0}}%\" style=\"min-width: 0;\" ng-transclude></div>\n" +
        "</div>\n" +
        "");

    // ADD MORE UNDER HERE IF NEEDED
}

export default initAngularTemplatedDirectives;
